import * as React from "react";
import { useTranslation } from 'react-i18next';
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Stack,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { JsonForms } from "@jsonforms/react";
import {
  materialRenderers,
  materialCells,
} from "@jsonforms/material-renderers";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import AppContext from "../../utils/context";
import CustomJsonForm from "../../components/customform";
import { buildSchema } from "../../utils/schema";

export default function ProjectDialog(props) {
  const { events, database } = React.useContext(AppContext);
  const { t } = useTranslation(); // Utiliser la fonction de traduction
  const [formData, setFormData] = React.useState({});
  const [formErrors, setFormErrors] = React.useState(null);
  const [project, setProject] = React.useState(null);
  const [projectTemplate, setProjectTemplate] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [callback, setCallback] = React.useState(null);
  const [isDrawer, setDrawer] = React.useState(false);
  const [workspace, setWorkspace] = React.useState(null);
  const [hasTriedToSave, setHasTriedToSave] = React.useState(false);
  const [readAccess, setReadAccess] = React.useState([]);
  const [writeAccess, setWriteAccess] = React.useState([]);
  const [notAllowedUsers, setNotAllowedUsers] = React.useState([]);
  const [schema, setSchema] = React.useState({});
  const [uischema, setUischema] = React.useState({});

  const helpProjectMsg = t('projects.help.message', { defaultValue: undefined });
  const helpProjectLink = t('projects.help.url', { defaultValue: undefined });

  React.useEffect(() => {
    const offCreate = events.on("create-project", handleCreate);
    const offEdit = events.on("edit-project", handleEdit);

    // Cleanup on unmount
    return () => {
      offCreate();
      offEdit();
    };
  }, [events]);

  const handleClose = () => {
    // Réinitialisation des états lors de la fermeture du formulaire
    setOpen(false);
    setFormData({});
    setTemplates([]);
    setCallback(null);
    setWorkspace(null);
    setDrawer(false);
    setHasTriedToSave(false);
    setFormErrors(null); // Réinitialisation des erreurs
  };

  const handleCloseForm = () => {
    // Réinitialisation des états lors de la fermeture du formulaire
    setOpen(false);
    setFormData({});
    setCallback(null);
    setHasTriedToSave(false);
    setFormErrors(null); // Réinitialisation des erreurs
  };

  const handleCreate = async (e) => {
    try {
      const { data: templatesData, error } = await database
        .from("project_templates_workspaces")
        .select("project_templates(id, name, description)")
        .eq("workspace_id", e.data.id);

      if (error) throw error;

      setWorkspace(e.data);
      setTemplates(
        templatesData.map(({ project_templates }) => project_templates)
      );
      setCallback(() => e.callback);
      setDrawer(true);

    } catch (error) {
      events.dispatch("alert-message", {
        data: {
          message: t('project_templates.loading.error'),
          severity: "error",
        },
      });
    }
  };

  const handleEdit = async (e) => {

    if (e?.data?.id) {
      const { data: projectData, errorP } = await database
        .from("projects")
        .select("id,metadata,workspace_id,project_templates(id,name,jsonschema,jsonschema_form)")
        .eq("id", e.data.id)
        .single();

      if (errorP) {
        events.dispatch("alert-message", {
          data: {
            message: t('project_templates.loading.error'),
            severity: "error",
          },
        });
        return;
      }
      const r = await buildSchema(
        database,
        projectData.project_templates.jsonschema, {
        workspace_id: projectData?.workspace_id,
        project_template_id: projectData?.project_templates.id,
        project_id: projectData?.id
      })

      setProject(projectData);
      setProjectTemplate(projectData.project_templates);
      setSchema(r);
      setUischema(projectData.project_templates.jsonschema_form);
      setFormData(projectData.metadata);
    }
    else {
      const { data: projectTemplateData, error } = await database
        .from("project_templates")
        .select("id,name,description,jsonschema,jsonschema_form")
        .eq("id", e.data.project_template_id)
        .single();

      if (error) {
        events.dispatch("alert-message", {
          data: {
            message: t('project_templates.loading.error'),
            severity: "error",
          },
        });
        return;
      }

      const r = await buildSchema(
        database,
        projectTemplateData.jsonschema, {
        workspace_id: workspace?.id,
        project_template_id: projectTemplateData.id,
        project_id: null
      })

      setProject(e.data);
      setProjectTemplate(projectTemplateData);
      setSchema(r);
      setUischema(projectTemplateData.jsonschema_form);
      setFormData({});
    }

    setCallback(() => e.callback);
    setOpen(true);
  };

  const save = async () => {
    setHasTriedToSave(true); // L'utilisateur tente de sauvegarder

    if (formErrors && formErrors.length) {
      events.dispatch("alert-message", {
        data: { message: t('commons.errors.fix_errors'), severity: "error" },
      });
      return;
    }

    try {
      const query = project?.id
        ? database
          .from("projects")
          .update({
            metadata: formData,
          })
          .eq("id", project.id)
          .select()
        : database
          .from("projects")
          .insert({
            workspace_id: project.workspace_id,
            project_template_id: project.project_template_id,
            metadata: formData
          })
          .select()

      const { data, error } = await query;

      if (error) {
        events.dispatch("alert-message", {
          data: {
            message: t('projects.save.error'),
            severity: "error",
          },
        });
        return;
      }

      if (callback) callback(!error ? data[0] : false);
      if (!error) handleClose(); // Fermer le formulaire seulement si la sauvegarde réussit
    } catch (error) {
      events.dispatch("alert-message", {
        data: {
          message: t('projects.save.error'),
          severity: "error",
        },
      });
    }
  };

  const saveProjectAccess = async (projectId, readAccess, writeAccess, notAllowedUsers) => {
    try {
      const userIdsRead = readAccess.filter(item => item.type === 'user').map(item => item.id);
      const userIdsWrite = writeAccess.filter(item => item.type === 'user').map(item => item.id);
      const userIdsNotAllowed = notAllowedUsers.map(item => item.id);
      const roleIdsRead = readAccess.filter(item => item.type === 'role').map(item => item.id);
      const roleIdsWrite = writeAccess.filter(item => item.type === 'role').map(item => item.id);

      const { error } = await database.rpc('manage_project_users_roles', {
        qproject_id: projectId,
        user_ids_read: userIdsRead,
        user_ids_write: userIdsWrite,
        user_ids_not_allowed: userIdsNotAllowed,
        role_ids_read: roleIdsRead,
        role_ids_write: roleIdsWrite
      });

      if (error) {
        events.dispatch("alert-message", {
          data: {
            message: t('projects.save.error'),
            severity: "error",
          },
        });
        return false;
      }

      events.dispatch("alert-message", {
        data: {
          message: t('projects.save.success'),
          severity: "success",
        },
      });
      return true;
    } catch (err) {
      events.dispatch("alert-message", {
        data: {
          message: t('projects.save.error'),
          severity: "error",
        },
      });
      return false;
    }
  };

  const addProject = (template) => {
    handleEdit({
      data: {
        id: null,
        name: "",
        description: "",
        workspace_id: workspace.id,
        project_template_id: template.id,
        metadata: {}
      },
      callback,
    });
  };

  const getTemplateName = (id) => {
    const template = templates.find((template) => template.id === id);
    return template?.name || "";
  };

  const displayTemplateDescription = () => {
    const template = templates.find(
      (template) => template.id === formData.template
    );
    return <div>{template?.description}</div> || "";
  };

  const setFormResult = ({ data, errors }) => {
    setFormData(data);
    setFormErrors(errors);
  };

  return (
    <React.Fragment>
      <Drawer
        open={isDrawer}
        anchor="right"
        sx={{ overflow: "none" }}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setDrawer(false);
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Typography>{t('projects.save.new')}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setDrawer(false)}>{t('buttons.close')}</Button>
        </DialogTitle>
        <DialogContent
          sx={{
            width: {
              xs: "90vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            }
          }}
        >
          {helpProjectMsg &&
            <Alert severity="info" onClose={() => { }}
              action=
              {helpProjectLink && <Button color="inherit" size="small" onClick={() => {
                window.open(helpProjectLink, "_blank");
              }}>
                {t('buttons.help')}
              </Button>}
            >
              {helpProjectMsg}
            </Alert>}

          <List sx={{ width: "100%" }}>
            {templates.map((template) => (
              <ListItem
                key={template.id}
                secondaryAction={
                  <Button onClick={() => addProject(template)}>{t('buttons.select')}</Button>
                }
              >
                <ListItemText primary={template.name} secondary={template.description} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Drawer>

      <Drawer
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
        anchor="right"
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Typography>{t('projects.save.new')} ({getTemplateName(formData.project_template_id)})</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button
              onClick={save}
              autoFocus
              color="success"
              variant="outlined"
              startIcon={<SaveAltIcon />}
            >
              {t('buttons.save')}
            </Button>
            <Button onClick={handleCloseForm}>{t('buttons.cancel')}</Button>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            width: {
              xs: "90vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            }
          }}
        >
          <Box sx={{ width: "100%", paddingTop: "20px" }}  className="jsonforms">
            <CustomJsonForm
              schema={schema}
              uischema={uischema}
              data={formData}
              onChange={({ data,errors }) => setFormResult({ data, errors })}
                contextVars={{
                    workspace_id: workspace?.id,
                    project_template_id: projectTemplate?.id,
                    project_id: project?.id,
                }}
              validationMode={hasTriedToSave ? 'ValidateAndShow' : 'ValidateAndHide'}
              config={{ showUnfocusedDescription: true }}
            />
            {displayTemplateDescription()}
          </Box>
        </DialogContent>
      </Drawer>
    </React.Fragment>
  );
}
